/* GravityLab 落地页 — 滚动动效
 *
 * 所有滚动进场 / stagger / 数字滚动 / 环境微动效统一用 GSAP。
 * hover / focus 等基础状态交给 CSS。整体尊重 prefers-reduced-motion。
 *
 * 触点：data-reveal / data-reveal-stagger / data-count / data-lines /
 *       各 section 自己的 data-* 钩子。
 *
 * ── 相对交付包的四处改动（其余逐字保留）────────────────────────────
 *
 * 1) 依赖从 CDN ESM 改为本地 UMD 全局
 *    交付包用 <script type="importmap"> 把 gsap 指向 cdn.jsdelivr.net，
 *    交接说明第 1 条提醒"若 Shopify 限制外域脚本需放行该域名"。
 *    这里改成主题自带的 assets/gl-gsap.js + gl-scrolltrigger.js（3.13.0 UMD），
 *    外部依赖归零，CSP 问题不存在，也少两跳跨域连接。
 *    注意主题里另有 gsap.min.js(3.0.4) 与 ScrollTrigger.min.js(3.8.0)，
 *    版本互不兼容且全部被注释无人加载，我们不碰它们，用独立命名的副本。
 *
 * 2) measureChrome()
 *    CSS 有 14 处依赖 --gl-chrome-h（3S 三页堆叠的 top 与行程、身份屏字号 calc、
 *    用户墙 pin、生产环节内边距、锚点 scroll-padding）。交付包那是硬编码 96px
 *    ＝它自带的公告条 36 + 固定 nav 60。主题的 header 高度由 header 设置、
 *    公告条开关、多语言选择器共同决定，所以实测而不是猜。
 *
 * 3) killDetached()
 *    主题编辑器换掉某个 section 时会整块替换 DOM，旧的 ScrollTrigger 仍注册着、
 *    指向已脱离文档的元素，位置判定会错乱（pin 段尤其明显）。
 *
 * 4) 启动块改为 Shopify 生命周期
 *    监听 shopify:section:load / unload / select。
 */

/* GSAP 由 assets/gl-gsap.js 与 gl-scrolltrigger.js 以 UMD 挂在 window 上。
 * 本文件以 <script type="module"> 加载，所以顶层作用域是模块私有的——
 * 25 个函数名不会污染全局，也不会和主题存量脚本撞名。 */
const gsap = window.gsap;
const ScrollTrigger = window.ScrollTrigger;

if (!gsap || !ScrollTrigger) {
  /* GSAP 没加载上（网络失败、资产被删）时的降级：
   * 把所有靠 JS 加 is-on 才显形的元素直接点亮，并把数字填成终值。
   * 内容本来就全在 HTML 里（SEO 契约），所以降级后页面完整可读，只是没有动效。 */
  document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-on'));
  document.querySelectorAll('[data-lines]').forEach((el) => el.classList.add('is-on'));
  console.warn('[GravityLab] GSAP 未加载，滚动动效已降级为静态呈现。');
} else {

gsap.registerPlugin(ScrollTrigger);

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── 站点框架高度实测 ──────────────────────────────────────────────
 * 主题的 .section-header 在 sticky_header_type: always 下是
 * position: sticky; top: 0，也就是**常驻覆盖**内容的那一层，正是
 * --gl-chrome-h 要表达的东西。公告条不 sticky（会滚走），不计入。
 *
 * 兜底：CSS 里 --gl-chrome-h 默认 96px，JS 没跑或选不中 header 时沿用。 */
function measureChrome() {
  const header = document.querySelector('.section-header');
  if (!header) return;
  const h = Math.round(header.getBoundingClientRect().height);
  if (h > 0) document.documentElement.style.setProperty('--gl-chrome-h', h + 'px');
}

function init() {
  measureChrome();
  sectionNav();
  tabs();
  if (reduce) {
    document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-on'));
    document.querySelectorAll('[data-count]').forEach((el) => { el.textContent = formatCount(el, parseFloat(el.dataset.count)); });
    return;
  }
  heroTyper();
  heroPin();
  obsxPin();
  sagGauge();
  /* linesUp 必须在 idfPin 之前：身份屏的标题用 data-lines-scrub，
   * 由 idfPin 的时间轴驱动，拆分得先完成，否则拿不到字符列表。 */
  linesUp();
  idfPin();
  fdrPin();
  ansCover();
  sVideos();
  spageCurrent();
  heroVideoIdle();
  pxaCards();
  pxaFade();
  thirdIn();
  enjoyRail();
  endingType();
  revealOnScroll();
  countUp();
  ScrollTrigger.refresh();
}

/* 章节导航（08-21）：滚动时高亮当前段 + 点击平滑跳转。
 * ⚠️ 高亮判定用**实际渲染位置**（每帧读 getBoundingClientRect，找占据视口中线的那一段），
 *    不用 IntersectionObserver 的可见比例：本页有 pin（身份屏）和 sticky 堆叠（3S 三页），
 *    元素在文档流里的静态位置与它真正显示的时机完全脱钩——按比例判定会高亮到错的段
 *    （数字滚动那次踩过同一个坑，见 countUp 的注释）。
 * 读取集中在一次 rAF 里，滚动事件本身只置一个脏标记，不做任何布局查询。 */
function sectionNav() {
  const nav = document.querySelector('[data-snav]:not([data-snav-bound])');
  if (!nav) return;
  nav.setAttribute('data-snav-bound', '1');
  const items = [...nav.querySelectorAll('[data-snav-item]')];
  const pairs = items
    .map((a) => ({ a, sec: document.querySelector(a.getAttribute('href')) }))
    .filter((p) => p.sec);
  if (!pairs.length) return;
  /* 深色段名单（标签常显之后必须按底色换配色，见 components.css 的 .snav.is-on-dark）：
   * 首屏影片、3S 封面、生产环节实拍、落版、身份屏（橙底也按"深"处理）。 */
  const darkZones = ['[data-hero-typer]', '.ans', '.pxa__stage', '.endg', '.idf']
    .flatMap((s) => [...document.querySelectorAll(s)]);

  let dirty = true;
  const paint = () => {
    if (dirty) {
      dirty = false;
      /* 判定线取视口中线：既不会因为段落刚露头就切换，也不会等它占满才切换。
       * 命中"跨过中线"的段落里取最后一个——sticky 堆叠时多段同时跨线，
       * 后面那个才是正压在上面、用户真正看到的。 */
      /* 判定「当前落在哪个区间」而不是「哪一段跨过中线」（08-21 修）：
       * 导航只列 5 个落点，中间还夹着身份屏、Henry、生产环节这些没有入口的段。
       * 用"跨线"判定时，走到这些段里五个目标段没有一个跨线，hit 就退回初始值 0、
       * 高亮跳回 Top（被反馈：滚到身份屏时亮的是第一项）。
       * 改成取「段顶已越过中线的最后一个」——它天然表达"我现在在这个落点之后"，
       * 空隙段落自动归属上一个落点：身份屏归 The problem、生产环节归 Our answer，
       * 与导航的分组口径一致（生产环节是 Our answer 的展开）。 */
      const line = window.innerHeight / 2;
      let hit = 0;
      pairs.forEach((p, i) => {
        if (p.sec.getBoundingClientRect().top <= line) hit = i;
      });
      pairs.forEach((p, i) => p.a.classList.toggle('is-active', i === hit));
      /* 首屏不显示导航（08-21）：第一屏留给影片与打字机的沉浸感。
       * 阈值 0.9 屏＝首屏 pin 行程（heroPin 的 '+=90%'）走完的位置，
       * 也正是洞察墙压上来的时刻，所以淡入与第二屏进场同步。
       * 判定顺手放在这一帧里，不额外挂监听。 */
      nav.classList.toggle('is-on', window.scrollY > window.innerHeight * 0.9);
      /* 底色判定：哪一段的**渲染矩形**覆盖了导航的中心点，就按那一段的底色配色。
       * 必须用 getBoundingClientRect：名单里有 pin（身份屏）和 sticky（3S 封面），
       * 文档位置与显示时机脱钩，按文档位置判会在错误的段落里换色。 */
      const midY = window.innerHeight / 2;
      const onDark = darkZones.some((el) => {
        const r = el.getBoundingClientRect();
        return r.top <= midY && r.bottom >= midY;
      });
      nav.classList.toggle('is-on-dark', onDark);
    }
    requestAnimationFrame(paint);
  };
  requestAnimationFrame(paint);
  window.addEventListener('scroll', () => { dirty = true; }, { passive: true });
  window.addEventListener('resize', () => { dirty = true; });

  /* 点击跳转：**瞬时**，不用 smooth（08-21 修）。
   * ⚠️ smooth 会让浏览器把中间每一个滚动位置都走一遍，于是途经段落的 scrub 动效
   *    （3S 堆叠、身份屏中缝拉开、用户墙火车）和 once 入场会被一路快速触发完——
   *    看起来就是"跳过去的路上所有动效自己快进播完了"（被反馈）。
   *    瞬时跳转只会落在目标位置那一个状态上，目标段的动效保持初始态、等用户自己滑才推进。
   * 位置用 scrollIntoView(block:'start') 而不是手算 offsetTop：
   *    pin 段被 GSAP 包进 pin-spacer，offsetTop 指向 spacer 而不是它显示的位置；
   *    段顶对齐视口顶时，pin 的 start（'top top'）恰好 progress = 0，正是初始态。 */
  pairs.forEach(({ a, sec }) => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      sec.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  });

}

function revealOnScroll() {
  document.querySelectorAll('[data-reveal]:not([data-bound])').forEach((el) => {
    el.setAttribute('data-bound', '1');
    const delay = parseInt(el.dataset.revealStagger || '0', 10) / 1000;
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 0.76,
      delay,
      ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 86%' },
      onStart: () => el.classList.add('is-on'),
    });
  });
}

/* 仪表数字滚动（支持小数、千分位、前后缀） */
function formatCount(el, v) {
  let n = v.toFixed((el.dataset.count.split('.')[1] || '').length);
  if (el.dataset.group === 'true') n = Number(n).toLocaleString('en-US');
  return (el.dataset.prefix || '') + n + (el.dataset.suffix || '');
}
function animateCount(el) {
  if (el.dataset.counting) return;
  el.dataset.counting = '1';
  const obj = { v: 0 };
  gsap.to(obj, {
    v: parseFloat(el.dataset.count),
    duration: 1.5,
    ease: 'power2.out',
    onUpdate: () => { el.textContent = formatCount(el, obj.v); },
    onComplete: () => { el.dataset.counting = ''; },
  });
}
function countUp() {
  /* 必须用 IntersectionObserver，不能用 ScrollTrigger 的位置触发：
   * 3S 数据条位于 sticky 堆叠页内，元素在文档流里的静态位置和它真正露出的时机完全脱钩，
   * ScrollTrigger 会在页面还没被拉上来时就把数字跑完（已踩过这个坑）。IO 按真实视口判定。 */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      animateCount(entry.target);
    });
  }, { threshold: 0.6 });
  document.querySelectorAll('[data-count]:not([data-bound])').forEach((el) => {
    el.setAttribute('data-bound', '1');
    el.textContent = formatCount(el, 0);
    io.observe(el);
  });
}

/* 生产环节 · 满屏实拍 + 卡片轮播（08-18 第三版，参考 yucca.co.za 的 solutions 段）
 * 演进：横向滑动画廊（pin 强制滑完，被否）→ 折叠手风琴 → 现在这一版。
 * 五张卡横排，当前那张变实心白卡并展开内容，其余是毛玻璃卡；背景换成该环节的实拍。
 * 五张卡的"环节感"靠三件事（参考站只有 3 张、天然放得下，我们得额外解决）：
 *   每张卡都带编号 · 五张同屏不切边 · 箭头旁的 01/05 读数。 */
function pxaCards() {
  const sec = document.querySelector('[data-pxa]:not([data-pxa-bound])');
  if (!sec) return;
  sec.setAttribute('data-pxa-bound', '1');
  const cards = [...sec.querySelectorAll('[data-pxa-card]')];
  const bgs = [...sec.querySelectorAll('.pxa__bg')];
  const countEl = sec.querySelector('[data-pxa-count]');
  const prevBtn = sec.querySelector('[data-pxa-prev]');
  const nextBtn = sec.querySelector('[data-pxa-next]');
  if (cards.length < 2) return;
  sec.classList.add('pxa--js'); /* 接管轮播（无 JS 时卡片全部展开成纵向列表） */
  let cur = 0;
  const show = (i) => {
    const n = (i + cards.length) % cards.length;
    if (n === cur) return;
    cards.forEach((c, k) => {
      c.classList.toggle('is-open', k === n);
      c.setAttribute('aria-expanded', k === n ? 'true' : 'false');
    });
    /* 背景交叉淡入：只动 opacity，不动 display——切换时两张要同时在，才叠得出来 */
    bgs.forEach((b, k) => {
      gsap.to(b, { autoAlpha: k === n ? 1 : 0, duration: 0.9, ease: 'power2.inOut' });
      b.classList.toggle('is-on', k === n);
    });
    if (countEl) countEl.textContent = String(n + 1).padStart(2, '0');
    cur = n;
  };
  /* 5s 一轮（08-18 两轮调整：6s 太慢 → 3s 太快 → 5s）。
   * 展开卡里有一句标题 + 三行说明要读，比洞察墙那种"只有画面"的轮播需要更长的停留。 */
  /* 入场（08-18 指定顺序）：背景图先淡入 → 过半时主标题逐字错峰 + 五张卡片同时淡入升起。
   * 用时间驱动 + once：这是揭幕动作，不该跟着滚动来回进退。
   * 背景层与暗角要**一起**淡入——只淡背景的话，一开始看到的是一层纯黑遮罩。 */
  const bgWrap = sec.querySelector('.pxa__bgs');
  const scrim = sec.querySelector('.pxa__scrim');
  const title = sec.querySelector('[data-lines-scrub]');
  /* ⚠️ 初始态必须用 gsap.set 显式写，不能靠 from() 的 immediateRender：
   * 在 **paused** 时间轴里，位置不在 0 的 from() 不会提前把初始值写进元素——
   * 于是卡片一直是 opacity 1，滑到这一段只看到"已经在那"的结果（实测踩到）。
   * 标题的 yPercent 120 由 linesUp 负责（它本来就是 set），这里不用重复。 */
  const veil = [bgWrap, scrim].filter(Boolean);
  if (veil.length) gsap.set(veil, { autoAlpha: 0 });
  gsap.set(cards, { autoAlpha: 0, y: 28 });
  const intro = gsap.timeline({ paused: true });
  /* 1.8s（原 1.1s 太快、几乎察觉不到就满了）。标题与卡片仍在 0.6 起，
   * 于是背景淡入的后 2/3 与它们重叠——先看到画面浮出来，再看到字和卡片跟上。 */
  if (veil.length) intro.to(veil, { autoAlpha: 1, duration: 1.8, ease: 'power2.out' }, 0);
  if (title && title._glWords) {
    /* 错峰比例与全站大字报一致（stagger 总量 ÷ 单字时长 ≈ 1.55） */
    const n = title._glWords.length;
    intro.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.62,
      stagger: { each: 1.55 * 0.62 / n } }, 0.6);
  }
  intro.to(cards, { autoAlpha: 1, y: 0, duration: 0.85,
    stagger: 0.075, ease: 'power3.out' }, 0.6);
  /* ⚠️ trigger 必须挂在**卡片行**上，不能挂 section：
   * 这一段的 stage 有一整屏高，section 顶到视口 75% 时卡片行还在屏外 67px（实测），
   * 整套入场就在看不见的地方跑完了——现象就是"入场没实现"。
   * 卡片行是这条时间轴里最晚露出的元素，用它做触发点，标题（在它上方约 180px）
   * 和满屏背景自然都已在视口内。 */
  const introTrigger = sec.querySelector('.pxa__cards') || sec;
  ScrollTrigger.create({ trigger: introTrigger, start: 'top 92%', once: true,
    onEnter: () => intro.play() });

  const AUTO = 5000;
  let timer = 0;
  const stop = () => { if (timer) { clearInterval(timer); timer = 0; } };
  const start = () => { stop(); timer = setInterval(() => show(cur + 1), AUTO); };
  ScrollTrigger.create({
    trigger: sec, start: 'top 80%', end: 'bottom 20%',
    onToggle: (self) => { if (self.isActive) start(); else stop(); },
  });
  /* 点卡片 / 按箭头都重置计时（否则刚点完可能立刻被自动轮播切走） */
  cards.forEach((c, i) => {
    c.addEventListener('click', () => { show(i); start(); });
    c.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(i); start(); }
    });
  });
  if (prevBtn) prevBtn.addEventListener('click', () => { show(cur - 1); start(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { show(cur + 1); start(); });
  /* 悬停暂停只挂在**卡片行**上，不是整段（08-19 修，与洞察墙同一个坑）：
   * 这一段有一整屏高，用滚轮滑过来时鼠标必然落在段内，整段 mouseenter 一触发就 stop——
   * 进到这一屏轮播根本没启动过（被反馈）。
   * 卡片行 hover 时暂停仍然保留：那是用户准备点选、或正在读展开卡里的说明句。 */
  /* ⚠️ 悬停暂停只在**真的有鼠标**的设备上挂（hover: hover + pointer: fine）：
   * 触屏浏览器会为一次点按合成一个 mouseenter，而对应的 mouseleave 常常永远不来——
   * 于是手指碰过卡片一次，自动轮播就永久停住（被反馈"这里缺少了自动轮播"）。 */
  const hoverZone = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ? sec.querySelector('.pxa__cards') : null;
  if (hoverZone) {
    hoverZone.addEventListener('mouseenter', stop);
    hoverZone.addEventListener('mouseleave', () => { if (timer === 0) start(); });
  }
}

/* 创始人引言 · 滚动驱动逐字（不 pin）：
 * 大字报级标题不能用时间驱动进场——字数多、错峰总时长两秒以上，
 * 用户以正常速度滑过时动画才跑到一半，看到的永远是已经完整的标题（换触发点解决不了）。
 * 改成滚动驱动后"滑多少出多少"，慢滑就能看清逐字错峰。 */
function fdrPin() {
  const sec = document.querySelector('[data-fdr]:not([data-fdr-bound])');
  if (!sec) return;
  sec.setAttribute('data-fdr-bound', '1');
  const title = sec.querySelector('[data-lines-scrub]');
  const attr = sec.querySelector('[data-fdr-attr]');
  const bg = sec.querySelector('[data-fdr-bg]');
  if (!title) return;
  if (attr) gsap.set(attr, { opacity: 0, y: 26 });
  /* 画面先渐显，文字后进（08-18 指定顺序）。淡入用**时间**驱动（once 触发）而不是 scrub——
   * 它只是揭幕，不该跟着滚动来回进退。
   * 起点 top 85% 比文字的 top 50% 早 35vh，正常滑速下 1.2s 的淡入到文字起跳时已经走完。
   * ⚠️ 淡入目标是**取景框整块**（data-fdr-bg 挂在框上），不是里面的 video：
   *    只淡入画面的话，空框会先立在那儿、画面再浮出来，读起来像图没加载完（踩过）。 */
  if (bg) {
    gsap.set(bg, { autoAlpha: 0 });
    ScrollTrigger.create({
      trigger: sec, start: 'top 85%', once: true,
      onEnter: () => gsap.to(bg, { autoAlpha: 1, duration: 1.2, ease: 'power2.out' }),
    });
  }
  /* **不 pin**，用段落自身进入视口的那段滚动驱动：
   * 这一段是从下方压上去覆盖 3S 的（z-index 5 + 负 margin），它从 3S 脱粘那一刻就开始露头。
   * 若把逐字挂在 pin 时间轴上，pin 要等段顶对齐视口顶才开始，中间近 900px 的滚动里
   * 这块区域只有底色——交接瞬间先看到一片白才看到字（已踩坑）。
   * 挂到 'top 80%'→'top 15%' 这段之后，段落一露头字就跟着出，白屏消失且全程可见。 */
  const tl = gsap.timeline({
    /* 段顶到视口一半才起（08-13 按指定位置）：这块标题近 600px 高，
     * 起太早时它整块还在屏幕下缘，逐字看不清；到 12% 时标题已完整可见、动效正好走完。 */
    scrollTrigger: { trigger: sec, start: 'top 50%', end: 'top 12%', scrub: 0.4 },
  });
  if (title._glWords) {
    /* 错峰量按"stagger 总量 ÷ duration ≈ 1.55"定——这是身份屏与 3S 封面的既有水平，
     * 全站大字报标题共用这一个比例，字符间的落差手感才一致。
     * 52 字符 × 0.0185 ≈ 0.96，除以 duration 0.62 得 1.55。 */
    tl.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.62,
      stagger: { each: 0.0185 } }, 0);
  }
  if (attr) tl.to(attr, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.16 }, 0.72);

  /* 景深退场（08-18）：这一段往上滚出时整块内容后退——scale 缩到 0.92、透明度到 0.45，
   * 由滚动 scrub 推进，所以"退多远"始终跟手上的滚动量一致。
   * ⚠️ transform 只作用在 **.fdr__inner** 上，不在 section 上：
   *    section 带 z-index 5、要从下方覆盖 3S 三页，给它加 transform 会新建层叠上下文，
   *    白白多一层风险；而且段落底色是出血的，缩它会在左右露出白边、看着像卡片缩小，
   *    只缩内容才是"内容退到景深后方、底色留在原处"。
   * 没有用 filter: blur —— 真正的景深感确实靠虚化，但整段做实时 blur 在低端机上会掉帧，
   *    这一段面积很大（整屏宽 × 500+ 高），代价与收益不成比例。 */
  const inner = sec.querySelector('.fdr__inner');
  if (inner) {
    gsap.to(inner, {
      scale: 0.92, opacity: 0.45, ease: 'none', transformOrigin: 'center top',
      scrollTrigger: { trigger: sec, start: 'bottom 92%', end: 'bottom top', scrub: 0.5 },
    });
  }
}

/* 通用 tab：面板原位切换 + 重新滚动数字（产品矩阵 / Triad 共用） */
function tabs() {
  document.querySelectorAll('[data-tabs]:not([data-tabs-bound])').forEach((group) => {
    group.setAttribute('data-tabs-bound', '1');
    const btns = [...group.querySelectorAll('[data-tab-btn]')];
    const panels = [...group.querySelectorAll('[data-tab-panel]')];
    btns.forEach((btn) => btn.addEventListener('click', () => {
      const key = btn.dataset.tabBtn;
      btns.forEach((b) => b.classList.toggle('is-active', b === btn));
      panels.forEach((p) => {
        const on = p.dataset.tabPanel === key;
        p.classList.toggle('is-active', on);
        if (!on) return;
        if (reduce) {
          p.querySelectorAll('[data-count]').forEach((el) => { el.textContent = formatCount(el, parseFloat(el.dataset.count)); });
          return;
        }
        gsap.fromTo(p, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' });
        p.querySelectorAll('[data-count]').forEach((el) => { el.dataset.counting = ''; animateCount(el); });
        ScrollTrigger.refresh();
      });
    }));
  });
}

/* 视频首屏 · 打字机双关（Addverb 式）：
 * 视频播放约 2s 后开始打字——左侧固定打出 INVISIBLE；
 * 右侧循环：逐字打出 GRAVITY → 停 → 逐字删除 → 打出 HERO → 停 → 删除…（"_" 光标恒闪，CSS 动画）。
 * 无 JS / reduced-motion：HTML 里保留完整词，静态显示。 */
function heroTyper() {
  const hero = document.querySelector('[data-hero-typer]:not([data-typer-bound])');
  if (!hero) return;
  hero.setAttribute('data-typer-bound', '1');
  const left = hero.querySelector('[data-type-left]');
  const right = hero.querySelector('[data-type-right]');
  if (!left || !right) return;
  const leftWord = left.textContent.trim();
  const words = (right.dataset.words || 'Gravity|Hero').split('|');
  left.textContent = '';
  right.textContent = '';

  const caretL = hero.querySelector('[data-caret-left]');
  const caretR = hero.querySelector('[data-caret]');
  const TYPE = 0.15, DEL = 0.075, HOLD = 1.8, REST = 0.45; // 08-11 调慢一档
  // 真打字机时序（光标三态：空闲闪烁 / 打字常亮随行 / 移交换位）：
  // 0–2s：左位光标就位、先闪（等视频进第 2 秒）
  if (caretL) caretL.classList.add('is-on');
  const solid = (c, on) => { if (c) c.classList.toggle('is-solid', on); };
  const tl = gsap.timeline({ delay: 2.0 });
  // 锁定右词容器宽度=最长词（此时字体已加载）：左缘固定，打字向右生长、删除向左收回
  const rightWrap = right.closest('.hv__word--right');
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), '');
  tl.call(() => {
    if (!rightWrap) return;
    right.textContent = longest;
    rightWrap.style.minWidth = `${Math.ceil(rightWrap.getBoundingClientRect().width)}px`;
    right.textContent = '';
  });
  // 逐字打出 INVISIBLE，光标常亮贴在字尾随行
  tl.call(() => solid(caretL, true));
  leftWord.split('').forEach((ch) => {
    tl.to({}, { duration: 0.11 });
    tl.call(() => { left.textContent += ch; });
  });
  // 打完左词：光标恢复闪烁一拍 → 移交到右位（左灭右亮）→ 再闪一拍
  tl.call(() => solid(caretL, false));
  tl.to({}, { duration: 0.5 });
  tl.call(() => {
    if (caretL) caretL.classList.remove('is-on');
    if (caretR) caretR.classList.add('is-on');
  });
  tl.to({}, { duration: 0.7 });
  // 右词打/删循环：打字与删除期间常亮，停留期间闪烁
  const loop = gsap.timeline({ repeat: -1, paused: true });
  words.forEach((w) => {
    loop.call(() => solid(caretR, true));
    w.split('').forEach((ch) => {
      loop.to({}, { duration: TYPE });
      loop.call(() => { right.textContent += ch; });
    });
    loop.call(() => solid(caretR, false));
    loop.to({}, { duration: HOLD });
    loop.call(() => solid(caretR, true));
    w.split('').forEach(() => {
      loop.to({}, { duration: DEL });
      loop.call(() => { right.textContent = right.textContent.slice(0, -1); });
    });
    loop.call(() => solid(caretR, false));
    loop.to({}, { duration: REST });
  });
  tl.call(() => loop.play());
}

/* 视频首屏 · 滚动上拉（Addverb 机制）：主标题+副文案在同一图层（布局即终态），
 * 初始整层下移 42vh——只露出主标题（居中）、副文案在屏外；滚动把整层拉回原位，
 * 副文案随层从底部到位。同时渐白幕布（超长渐变）整体上移，到位即全白，与第二屏无缝接色。
 * 背景视频与 SCROLL TO DISCOVER 全程不动。 */
function heroPin() {
  const hero = document.querySelector('[data-hero-typer]:not([data-pin-bound])');
  if (!hero) return;
  hero.setAttribute('data-pin-bound', '1');
  const stage = hero.querySelector('[data-hero-stage]');
  const sub = hero.querySelector('[data-hero-sub]');
  if (!stage) return;
  // 三段式（08-11 定稿）：
  // 第一段（pin 0–85%）：背景不动（无任何遮罩），主标题上移、副文案从画面底部滑上来（初始完全在屏外）；
  // 定格（pin 85–100%）：完整状态停住；
  // 第二段（pin 结束后）：再滑动，页面才往下走，第二屏（白色抽屉圆角卡）从底部进场。
  const scrollWrap = document.querySelector('[data-hero-scroll]');
  if (scrollWrap) scrollWrap.classList.add('is-fixed'); // pin 期间固定于底部（保持白色）
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: hero, start: 'top top', end: '+=90%',
      pin: true, scrub: 0.4, invalidateOnRefresh: true,
    },
  });
  tl.fromTo(stage,
    { y: () => window.innerHeight * 0.42 },
    { y: 0, ease: 'none', duration: 0.85 }, 0);
  /* 副文案额外下移一段：桌面它是 absolute top:48%，加 42vh(整层) + 22vh(自己) ≈ 112vh，
   * 首屏初始完全看不到它。
   * ⚠️ 窄屏它是**静态流**（排在标题下面，避免与标题重叠），静态位置由标题块的高度决定，
   *    比桌面的 48% 高得多——430×873 实测静态 top 只有 669，加 22vh(192) 之后
   *    顶边落在 861，屏底还露出 12px（被反馈"初始状态副文案在底部出现了"）。
   *    所以窄屏把系数提到 0.34，并额外加一段淡入：位移量随屏高比例变化，
   *    淡入是不管比例都成立的兜底。 */
  const narrow = window.matchMedia('(max-width: 760px)').matches;
  if (sub) {
    tl.fromTo(sub,
      { y: () => window.innerHeight * (narrow ? 0.34 : 0.22) },
      { y: 0, ease: 'none', duration: 0.85 }, 0);
    if (narrow) tl.fromTo(sub,
      { autoAlpha: 0 }, { autoAlpha: 1, ease: 'none', duration: 0.3 }, 0.15);
  }
  tl.to({}, { duration: 0.15 }); // 定格段
  /* SCROLL 提示：**一开始滑动就淡出**（放在时间轴 0 位，前 12% 行程内走完）。
   * ⚠️ 不能挂在末段（0.95）：pin 行程有 90vh，那样整个上拉过程里它都还在，
   *    而 takeaway 三行正从画面底部升上来，两者必定叠在一起（08-17 反馈过一次，
   *    08-19 从 08-13 的离线包恢复 motion.js 时又被带回了旧写法，这里是第二次修）。
   * ⚠️ 也不要用洞察墙做独立 trigger：它自身是 pin 的，pin-spacer 会让位置计算失准，
   *    结果提示在后续每一屏（含橙色身份屏）都残留可见。 */
  if (scrollWrap) tl.to(scrollWrap, { autoAlpha: 0, ease: 'none', duration: 0.12 }, 0);
}

/* 通用逐词遮罩错峰上升（data-lines / haptikos 式 word mask reveal）：
 * 行 = 子 <span>；行内按词拆分、每词独立遮罩，全篇按词序错峰升起——
 * 同一行内也不同步（参考 haptikos.tech：WHERE 半途时 HAND 已就位）。
 * 可选 data-lines-delay 等前置动效（如标题打字机）。 */
function linesUp() {
  document.querySelectorAll('[data-lines]:not([data-lines-bound])').forEach((el) => {
    el.setAttribute('data-lines-bound', '1');
    const mode = el.dataset.lines; // 'line' 整行 | 'char' 逐字错落 | 默认逐词
    const byLine = mode === 'line';
    const byChar = mode === 'char';
    const words = [];
    [...el.children].forEach((ln) => {
      /* 逐字模式单独走一条路径，为的是支持**行内着色**：
       * 一行里既有 ink 字又有琥珀字（<em class="fdr__hl">）时，
       * 原来的 ln.textContent 会把嵌套元素连 class 一起抹掉，颜色只能整行给。
       * ⚠️ 只取行内子元素的 class，行本身（ln）的 class 不复制——
       *    ans__fly-line 那类行有自己的定位样式，复制到每个字符上会散架。 */
      if (byChar) {
        const seq = [];
        const walk = (node, cls) => {
          [...node.childNodes].forEach((n) => {
            if (n.nodeType === 3) [...n.textContent].forEach((c) => seq.push([c, cls]));
            else if (n.nodeType === 1) walk(n, n.getAttribute('class') || cls);
          });
        };
        walk(ln, '');
        while (seq.length && !seq[0][0].trim()) seq.shift();
        while (seq.length && !seq[seq.length - 1][0].trim()) seq.pop();
        ln.textContent = '';
        /* 每个单词包一层 .w-word（inline-block + nowrap）。
         * ⚠️ 不包的话标题会在**单词中间**断行：逐字拆分后每个字符都是一个 inline-block，
         *    而 inline-block 之间是合法的软换行点，浏览器会照断——窄屏上出现过
         *    "OUR ANS / WERS"、"ENGINEERED, THEN PROVE / N."。
         *    单词内禁止换行、单词之间留普通空格（换行点在这里），断行就只发生在词边界。 */
        let word = null;
        seq.forEach(([ch, cls]) => {
          if (!ch.trim()) { word = null; ln.appendChild(document.createTextNode(' ')); return; }
          if (!word) {
            word = document.createElement('span');
            word.className = 'w-word';
            ln.appendChild(word);
          }
          const mask = document.createElement('span');
          mask.className = 'w-mask';
          const inner = document.createElement('span');
          inner.className = cls ? 'w-in ' + cls : 'w-in';
          inner.textContent = ch;
          mask.appendChild(inner);
          word.appendChild(mask);
          words.push(inner);
        });
        return;
      }
      const text = ln.textContent;
      ln.textContent = '';
      const parts = byLine ? [text.trim()]
        : byChar ? [...text.trim()]
        : text.split(/\s+/).filter(Boolean);
      parts.forEach((w, i) => {
        // 逐字模式下空格不包 mask（inline-block 会折叠空格）——用 nbsp 保留字距
        if (byChar && !w.trim()) { ln.appendChild(document.createTextNode('\u00A0')); return; }
        const mask = document.createElement('span');
        mask.className = 'w-mask';
        const inner = document.createElement('span');
        inner.className = 'w-in';
        inner.textContent = w;
        mask.appendChild(inner);
        ln.appendChild(mask);
        if (!byChar && i < parts.length - 1) ln.appendChild(document.createTextNode(' '));
        words.push(inner);
      });
    });
    /* data-lines-scrub：只做拆分与初始态，把字符交给**外部时间轴**驱动（不自建 ScrollTrigger）。
     * 用于必须和同屏其它动作严格排序的场合——身份屏（逐字 → 正文 → 中缝拉开）、
     * 3S 封面、Henry 引言、生产环节入场、用户墙（逐字走完才开火车）都靠它。
     * ⚠️ 这一段在 08-19 从 08-13 的离线包恢复 motion.js 时丢过一次（那时还没有这个机制）：
     *    少了它，所有 scrub 标题会各自挂上下面那条 top 96% 的独立触发器、在段落刚露头时
     *    就自己跑完，各段时间轴里的 `if (title._glWords)` 全部为假——表现就是"逐字不生效"。 */
    if (el.hasAttribute('data-lines-scrub')) {
      gsap.set(words, { yPercent: 120 });
      el._glWords = words;
      return;
    }
    gsap.fromTo(words,
      { yPercent: 120 },
      {
        yPercent: 0,
        duration: byChar ? 0.9 : byLine ? 0.95 : 0.8,
        ease: 'power4.out',
        // 逐字：stagger 远小于 duration → 字符依次拉起但相互重叠，形成错落的波
        stagger: byChar ? 0.028 : byLine ? 0.11 : 0.055,
        delay: parseFloat(el.dataset.linesDelay || '0'),
        scrollTrigger: { trigger: el, start: 'top 96%', once: true },
      });
  });
}

function idfPin() {
  const sec = document.querySelector('[data-idf]:not([data-idf-bound])');
  if (!sec) return;
  sec.setAttribute('data-idf-bound', '1');
  const top = sec.querySelector('[data-idf-top]');
  const bottom = sec.querySelector('[data-idf-bottom]');
  const video = sec.querySelector('[data-idf-video]');
  const sound = sec.querySelector('[data-idf-sound]');
  const title = sec.querySelector('[data-lines-scrub]');
  const sub = sec.querySelector('[data-idf-sub]');
  if (!top || !bottom) return;
  /* ── TVC 空缺档（08-22）─────────────────────────────────────────────
   * TVC 交付延后时，给 <section data-idf> 加 data-idf-film="none"，这一屏退化成
   * 纯大字报：裂开与放大整段不走，行程缩到只够文字进场，尾段把底色沉到 3S 封面的灰。
   * **不放任何"待补"标记**——观众不该看出这里本来有个画面，所以宁可少一层，不要空取景框。
   * 视频元素缺失时也走这条路（Colin 部署时若漏传 mp4，退化而不是白屏）。
   * TVC 到位：删掉属性 + 覆盖 assets/videos/film-tvc{,-m}.mp4，下面的完整路径一行没动。 */
  const filmOff = sec.getAttribute('data-idf-film') === 'none' || !video;
  /* 初始态：TVC 缩到中心一点且不可见（裂开前被上下两半橙底盖住，本来也看不到），
   * 圆角要有非 0 起点——时间轴末尾会把它归零，做"画面把屏幕顶开"的收边。 */
  if (!filmOff) gsap.set(video, { autoAlpha: 0, scale: 0.12, borderRadius: '10px' });
  /* 空缺档把视频**摘掉**而不只是隐藏：display:none 的 <video> 在 Chrome 里照样会选
   * <source> 并请求 metadata。摘掉之后这一段一个字节都不走。 */
  else if (video) video.remove();
  /* 空缺档（或 Colin 漏传 mp4）连按钮一起摘：没有画面时留一个"开声音"等于露馅。 */
  if (filmOff && sound) sound.remove();
  if (sub) gsap.set(sub, { opacity: 0, y: 26 });

  /* 底色与上一屏（洞察墙）**共享**一次白 → 琥珀的过渡：
   * 只染自己那一段的话，两段之间会留下一条硬边（改过三轮才定这个做法）。
   * 用 --gl-paper-0 那类透明阶不行——这里要的是整块底色一起变，只能按滚动进度写 backgroundColor。 */
  const rootCS = getComputedStyle(document.documentElement);
  const fromBg = rootCS.getPropertyValue('--gl-paper').trim();
  const toBg = rootCS.getPropertyValue('--gl-amber').trim();
  const prev = document.querySelector('[data-obsx]');
  const stage = sec.querySelector('.idf__stage');
  /* 窄屏空缺档＝**扁平档**：两半退出绝对定位（position: static），橙底搬到了 stage 上
   * （见 components.css 的窄屏块）。这一档里染色目标必须换成 stage，不能再染两半——
   * ⚠️ 08-27 踩过：两半退出定位后高度只剩内容高（219px / 68px），而 stage 是整屏。
   *    照旧染两半的话，过渡期间 stage 是纯橙、两半各是插值中的浅橙、sec 又是第三个值，
   *    屏幕上就出现三四条深浅不一的橙带（反馈"这里的渐变色怎么会那么怪异"）。
   * matchMedia 只在初始化时取一次：跨 760px 断点 resize 后目标不会重算，
   * 但那需要"空缺档 + 拖动窗口跨断点"同时成立，代价远低于每帧判断。 */
  const flat = filmOff && window.matchMedia('(max-width: 760px)').matches;
  const halves = flat ? (stage ? [stage] : []) : [top, bottom];
  const tinted = [sec].concat(halves).concat(prev ? [prev] : []);
  gsap.fromTo(tinted, { backgroundColor: fromBg }, {
    backgroundColor: toBg, ease: 'none',
    scrollTrigger: { trigger: sec, start: 'top 78%', end: 'top top', scrub: 0.3 },
  });

  if (filmOff) {
    /* 行程 90%（完整档 220%）。文字的**像素速度**要与完整档一致，不能照抄进度值：
     * 完整档里文字走 0→0.34 的 220% ＝ 75% 屏高，这里行程只有 90%，
     * 所以所有补间按 220/90 ≈ 2.44 倍放大 —— duration 0.1→0.244、
     * stagger 0.0042→0.0102、正文起点 0.28→0.68。逐字手感与完整档相同。 */
    const tl0 = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=90%',
        pin: true, scrub: 0.4, invalidateOnRefresh: true,
      },
    });
    if (title && title._glWords) {
      tl0.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.244,
        stagger: { each: 0.0102 } }, 0);
    }
    if (sub) tl0.to(sub, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.146 }, 0.68);
    /* 尾段把橙沉成 3S 封面的灰：完整档靠 TVC 满屏做橙→灰的缓冲，这里没有画面可缓冲，
     * 直接硬切会在两屏之间留一条色带。灰底白字是 3S 封面本来的配色，字照样读得清。
     * 只染自己这三块，**不带上洞察墙**——它已经由上面那段白→橙负责，再染会把它一起拖灰。 */
    const ansBg = rootCS.getPropertyValue('--gl-ans-bg').trim();
    /* 目标与上面那段白→橙**保持同一套**（扁平档染 stage、完整档染两半）。
     * 两段染的元素不一致的话，尾段会把上一段染过的元素留在橙色上不动。 */
    tl0.to([sec].concat(halves), { backgroundColor: ansBg, ease: 'none', duration: 0.16 }, 0.84);
    return;
  }

  /* 解除静音（10-26）。浏览器只在 user gesture 里放行"带声音的播放"，
   * 所以 video 始终 muted 起播，由这一下点击把 muted 翻掉。
   * 翻完补一次 play()：若此前因任何原因被暂停，这次调用带着 gesture，一定拉得起来。 */
  if (sound && video) {
    sound.addEventListener('click', () => {
      video.muted = !video.muted;
      sound.setAttribute('aria-pressed', String(!video.muted));
      if (!video.muted && video.paused) { const r = video.play(); if (r && r.catch) r.catch(() => {}); }
    });
  }

  /* 窄屏「银幕档」（10-26）：竖屏上 16:9 的片子按满屏 cover 要裁掉 74%
   * （实测 390×844 / 360×800 / 430×932 都是 74% 上下，只剩正中四分之一）。
   * 所以窄屏不追求满屏 —— 两半只退到正好把中缝撑成一条 16:9 横带，
   * 上下留住橙底当画框。退多少 = 横带半高 ÷ 半屏高 =（W×9/16 ÷ 2）÷（H ÷ 2）。
   * 桌面维持 100（两半完全退出、画面满屏），那一档本来就不裁。
   * 写成函数值配合 invalidateOnRefresh：转屏、改窗口宽高都会重算。 */
  const bandSplit = () => {
    if (!window.matchMedia('(max-width: 760px)').matches) return 100;
    const st = sec.querySelector('.idf__stage');
    const W = st ? st.clientWidth : window.innerWidth;
    const H = st ? st.clientHeight : window.innerHeight;
    if (!W || !H) return 100;
    return Math.min(100, (W * 9 / 16) / H * 100);
  };

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sec, start: 'top top', end: '+=220%',
      pin: true, scrub: 0.4, invalidateOnRefresh: true,
      /* 只在裂开之后才播 TVC：裂开前它完全被遮住，白解码 */
      onUpdate: (self) => {
        if (self.progress > 0.5) { if (video.paused) { const r = video.play(); if (r && r.catch) r.catch(() => {}); } }
        else if (!video.paused) video.pause();
        /* 声音按钮与起播同一个阈值：画面露出来了才给开声的入口 */
        if (sound) sound.classList.toggle('is-on', self.progress > 0.5);
      },
      onLeave: () => {
        if (!video.paused) video.pause();
        /* 滑走时收回声音与按钮状态：用户在别的段落不该被这条片子的声音追着跑 */
        if (sound) {
          video.muted = true;
          sound.setAttribute('aria-pressed', 'false');
          sound.classList.remove('is-on');
        }
      },
      onEnterBack: () => { const r = video.play(); if (r && r.catch) r.catch(() => {}); },
    },
  });
  // ① 标题逐字错峰（stagger 远小于单字时长 → 字符依次拉起但相互重叠，成波不成队）
  if (title && title._glWords) {
    tl.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.1,
      stagger: { each: 0.0042 } }, 0);
  }
  // ② 标题走完，正文才整体升起（与生产段章节正文同一手感：opacity + 26px）
  if (sub) tl.to(sub, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.06 }, 0.28);
  // ③ 定格：文字全部到位后留一段静止行程，读完再往下
  tl.to({}, { duration: 0.06 }, 0.34)
    .to(video, { autoAlpha: 1, ease: 'none', duration: 0.04 }, 0.36)
    /* ④ 中缝拉开：两半各退出自己的高度（= 半屏），标题跟着上半上移、正文跟着下半下移。
     * yPercent 而不是 y：两半高度是 50% 的百分比值，窗口变化时不必重算像素。 */
    .to(top, { yPercent: () => -bandSplit(), ease: 'power1.inOut', duration: 0.42 }, 0.4)
    .to(bottom, { yPercent: () => bandSplit(), ease: 'power1.inOut', duration: 0.42 }, 0.4)
    // TVC 与中缝同步同缓动放大：缝越宽、画面越大，读起来是"画面把屏幕顶开"
    .fromTo(video, { scale: 0.12 }, { scale: 1, ease: 'power1.inOut', duration: 0.42 }, 0.4)
    .to(video, { borderRadius: 0, ease: 'none', duration: 0.12 }, 0.7)
    /* ⑤ 防滑：满屏后留出约四分之一行程什么都不做。
     * 没有这段，TVC 刚满屏就被下一屏顶掉，来不及看。 */
    .to({}, { duration: 0.18 });
}

/* 3S 三页的页眉当前项高亮（08-27 反馈"难以快速判断正在浏览哪一条"）。
 * 三条页眉是 sticky 堆叠的，视觉上同时可见，所以必须显式标出"当前"。
 * ⚠️ 判定必须读 getBoundingClientRect —— 这一段是 sticky，元素在文档流里的
 *    静态位置与真正显示的时机完全脱钩（本页第五次踩同一个坑：snav 高亮、
 *    3S 视频暂停、snav 深色底判定、封面逐字进场，都是这个原因）。
 * 判定口径：页眉已经贴到堆叠区顶部（top ≤ 140）且这一页的内容区还有实质高度
 *    露在视口里（bottom > 260）→ 认为它是当前页；取满足条件的**最后一个**，
 *    因为后一页会压在前一页上面。 */
function spageCurrent() {
  const host = document.querySelector('.ansx:not([data-spc-bound])');
  if (!host) return;
  host.setAttribute('data-spc-bound', '1');
  const pages = [...host.querySelectorAll('.spage')];
  const heads = pages.map((p) => p.querySelector('.spage__head'));
  if (pages.length < 2) return;
  const sync = () => {
    /* 判定＝**段顶已越过判定线的最后一个**（与 sectionNav 同一套口径）。
     * ⚠️ 08-27 修：原来是区间判定 `top <= 140 && bottom > 260`，第三页（最后一页）
     *    滚到底时 bottom 已小于 260，条件永不成立 ⇒ 第三条当前时不高亮
     *    （反馈"第三个它当前的时候没有高亮"）。区间判定还有个隐患：三页都不满足时
     *    cur 静默退回 0，高亮跳回第一条。"越过线的最后一个"是单调的，
     *    最后一页必然能选中，也不存在无人命中的空档。
     * 线取**视口中线**。试过"页眉带下沿（页眉高 × 页数）"，不成立：
     * sticky 堆叠里第 n 页的 top 停在 n×head-h 就不再变小（实测 96 / 128 / 173），
     * 第二、三页永远越不过 96 这条线，中间那页从头到尾选不上。
     * 中线判定按"这一页的内容是否已占据视口主体"来读，实测三页依次命中：
     *   top = 96/937/2076 → 1   96/303/1442 → 2   96/128/807 → 2   96/128/173 → 3 */
    const line = window.innerHeight / 2;
    let cur = 0;
    pages.forEach((p, i) => {
      if (p.getBoundingClientRect().top <= line) cur = i;
    });
    heads.forEach((h, i) => { if (h) h.classList.toggle('is-current', i === cur); });
  };
  ScrollTrigger.create({ trigger: host, start: 'top bottom', end: 'bottom top',
    onUpdate: sync, onToggle: sync, onRefresh: sync });
  sync();
}

/* 用户墙 · 照片带横向滑入（pin 一屏）。
 * 顺序（08-18 指定）：滑到这一屏 → 文字逐字出场 → 全部就位后定格 → 再滑才开火车。
 * 行程分配：文字 18% · 定格 7% · 照片 75%。 */
function enjoyRail() {
  const sec = document.querySelector('[data-enjoy]:not([data-enjoy-bound])');
  if (!sec) return;
  sec.setAttribute('data-enjoy-bound', '1');
  const pinEl = sec.querySelector('[data-enjoy-pin]');
  const rail = sec.querySelector('[data-enjoy-rail]');
  if (!pinEl || !rail) return;

  /* ── 移动端（≤760px）：改成「文字进场 → 照片自动跑马灯」（08-21 指定）──
   * 桌面那套是 pin 整屏 + 纵滚映射横移（火车开过），手机上没有那么多滚动行程、
   * 也不该把纵向滚动劫持成横向。
   * ⚠️ 原来这里是直接 return，结果标题永远看不见——linesUp 对 data-lines-scrub 的元素
   *    只做拆分并把字符压到 yPercent 120 等外部时间轴驱动，return 之后没人驱动它
   *    （被反馈"ENJOY 那句文案消失了"）。所以移动端必须自己接管这段进场。
   * 跑马灯用 CSS 动画而不是 GSAP：它是匀速无限循环、与滚动无关，交给合成层最省电。
   * 轨道内容克隆一份，动到 -50% 时第二份正好接上，循环无缝。 */
  if (window.matchMedia('(max-width: 760px)').matches) {
    const title = sec.querySelector('[data-lines-scrub]');
    [...rail.children].forEach((n) => rail.appendChild(n.cloneNode(true)));
    /* 触发点从 'top 72%' 提到 'top bottom'——这一屏**刚露头**就开始跑，
     * 而且跑马灯与文字进场**同时**启动（原来排在文字之后 +0.15s）。
     * 原来的节奏照搬了桌面端"文字定格后才开火车"，但桌面那边火车是滚动映射的、
     * 用户自己在推；移动端是匀速 CSS 动画，等 0.85s 再动的结果就是
     * 滑到这一屏时照片还静止着（被反馈"不用等滑到这一屏才跑"）。 */
    ScrollTrigger.create({
      trigger: sec, start: 'top bottom', once: true,
      onEnter: () => {
        sec.classList.add('is-marquee');
        if (title && title._glWords) {
          const n = title._glWords.length;
          gsap.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.7,
            stagger: { each: 1.55 * 0.7 / n } });
        }
      },
    });
    return;
  }
  rail.querySelectorAll('img').forEach((img) => {
    if (!img.complete) img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  });
  const title = sec.querySelector('[data-lines-scrub]');
  /* 起点：轨道退到视口右缘、**只露出第一张的一角**（08-27 反馈）。
   * 原来整条退到 innerWidth 之外、初始完全不可见，配合 TEXT 0.18 + HOLD 0.07，
   * 意味着 pin 开始后要滚过 25% 的行程（1440 屏实测约 1137px ≈ 1.26 屏）
   * 照片区才第一次出现内容——被反馈"下面空白太多，会觉得页面已经结束了"。
   * 露 6% 的边缘就够：一进这一屏就知道右侧还有内容在等，不必等火车开到。 */
  const from = () => window.innerWidth * 0.94;
  const to = () => window.innerWidth - rail.scrollWidth;
  const dist = () => Math.max(1, from() - to());
  gsap.set(rail, { x: from });

  /* 轨道顶边＝**两段文案之间的中线**：不遮 "Enjoy what's playing."，刚好压住
   * "Forget what's holding it."——照片盖住"holding it"这句本身就是双关。
   * 必须实测而不是写死 40vh：字号是 clamp(9.03vw) 流体的，两段位置随视口宽度走。
   * 值写进 CSS 变量，样式出口仍然只有 CSS 一处。 */
  const lines = title ? [...title.children] : [];
  const setRailTop = () => {
    if (lines.length < 3) return;
    const a = lines[1].getBoundingClientRect();
    const b = lines[2].getBoundingClientRect();
    const mid = (a.bottom + b.top) / 2 - pinEl.getBoundingClientRect().top;
    rail.style.setProperty('--enjoy-rail-top', Math.round(mid) + 'px');
  };
  setRailTop();
  ScrollTrigger.addEventListener('refresh', setRailTop);

  /* 文字与定格各收一档（0.18/0.07 → 0.12/0.04）：照片起步从 progress 0.25 提到 0.16，
   * 1440 屏上提前约 409px。文字进场仍在 TEXT 内走完（末字落位公式见下方注释）。 */
  const TEXT = 0.12;
  const HOLD = 0.04;
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: pinEl, start: 'top top', end: () => '+=' + Math.round(dist() * 1.07),
      pin: true, scrub: 0.6, invalidateOnRefresh: true, anticipatePin: 1,
    },
  });
  if (title && title._glWords) {
    /* 错峰按全站比例（stagger 总量 ÷ 单字时长 ≈ 1.55），但**总时长必须收进 TEXT 内**：
     * 末字落位时刻 = (1.55 + 1) × d，要它等于 TEXT 就得 d = TEXT / 2.55。
     * ⚠️ 挂独立 ScrollTrigger 时 GSAP 会把补间归一化到区间，直接写 duration: TEXT 看不出问题；
     *    放进 timeline 后总时长就是真时长，照原样写会让末字拖到 progress 0.46 才落位，
     *    而照片 0.25 就起步了——"文字还在出、火车已经开"（实测踩到）。 */
    const n = title._glWords.length;
    const d = TEXT / 2.55;
    tl.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: d,
      stagger: { each: 1.55 * d / n } }, 0);
  }
  tl.to({}, { duration: HOLD }, TEXT)
    .fromTo(rail, { x: from }, { x: to, ease: 'none', duration: 1 - TEXT - HOLD }, TEXT + HOLD);
  window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
}

/* 落版打字机：先打出品牌名 → 停一拍 → 逐字删除 → 打出落版句，光标最后留在句尾闪烁。
 * 与首屏的 GRAVITY ⇄ HERO 打/删循环同一套语汇——首屏留悬念，这里给答案。
 * 光标三态与全站一致：打/删时常亮（is-solid），停留时闪烁（is-on）。 */
function endingType() {
  const sec = document.querySelector('[data-ending]:not([data-ending-bound])');
  if (!sec) return;
  sec.setAttribute('data-ending-bound', '1');
  const word = sec.querySelector('[data-ending-word]');
  const caret = sec.querySelector('[data-ending-caret]');
  if (!word) return;
  const BRAND = sec.dataset.endingBrand || 'Perlegear';
  const CLOSER = sec.dataset.endingCloser || word.textContent.trim() || 'Your invisible hero';
  /* 先按**最长那句**量一次宽度并锁死：不锁的话「」取景框会跟着字数一格格伸缩。
   * 用 JS 写 min-width 而不是 HTML 里的 style 属性——版面尺寸取决于渲染后的字宽，写不进静态 CSS。 */
  word.textContent = CLOSER;
  word.style.minWidth = Math.ceil(word.getBoundingClientRect().width) + 'px';
  word.textContent = '';
  if (caret) caret.classList.add('is-solid');

  const IN = 0.075;   /* 打字：每字 */
  const OUT = 0.045;  /* 删除比打字快——删是过程，不是内容 */
  const tl = gsap.timeline({ paused: true });
  let at = 0.25;
  for (let i = 1; i <= BRAND.length; i++) {
    tl.call(() => { word.textContent = BRAND.slice(0, i); }, null, at); at += IN;
  }
  at += 0.85; /* 停一拍：让品牌名读得完整 */
  for (let i = BRAND.length - 1; i >= 0; i--) {
    tl.call(() => { word.textContent = BRAND.slice(0, i); }, null, at); at += OUT;
  }
  at += 0.3;
  for (let i = 1; i <= CLOSER.length; i++) {
    tl.call(() => { word.textContent = CLOSER.slice(0, i); }, null, at); at += IN;
  }
  tl.call(() => {
    if (caret) { caret.classList.remove('is-solid'); caret.classList.add('is-on'); }
  }, null, at + 0.15);
  ScrollTrigger.create({ trigger: sec, start: 'top 70%', once: true, onEnter: () => tl.play() });
}

/* 打字机工厂：把元素文本改造为「逐字打出 + 光标随行」，返回 play/reset（可逆，供 scrub 驱动）。
 * 先锁定容器尺寸，打字过程中版面与「」角标框不跳动。 */
function makeTyper(host, innerSel) {
  const inner = innerSel ? host.querySelector(innerSel) : host;
  if (!inner) return null;
  const text = inner.textContent.trim();
  inner.style.minWidth = `${Math.ceil(inner.getBoundingClientRect().width)}px`;
  inner.textContent = '';
  const caret = document.createElement('span');
  caret.className = 'tt-caret';
  caret.textContent = '_';
  inner.appendChild(caret);
  let played = false;
  /* ⚠️ 必须留住这条时间轴的引用。played 只挡住"重复 play"，挡不住"reset 之后旧时间轴
   * 还在打字"——它是一串 tl.call，reset 把 DOM 清空了，它下一帧照样继续 caret.before()
   * 把剩下的字符塞回来。于是反向滚一下再滚回来，就变成
   * 「上一轮的残尾 + 这一轮从头打的全词」（实测复现出 "rtSmart"：
   * 第一轮打到第 3 个字符被 reset，它继续补完 "rt"，第二轮再从头打 "Smart"）。
   * 这一段是 scrub 驱动的，progress 本来就会因为 scrub 的平滑延迟和
   * ScrollTrigger.refresh() 而来回跨阈值，play/reset 交错是常态而不是边界情况。 */
  let tl = null;
  return {
    play() {
      if (played) return;
      played = true;
      gsap.set(host, { autoAlpha: 1 });
      tl = gsap.timeline();
      tl.call(() => caret.classList.add('is-on', 'is-solid'));
      text.split('').forEach((ch) => {
        tl.to({}, { duration: 0.055 });
        tl.call(() => caret.before(document.createTextNode(ch)));
      });
      tl.call(() => caret.classList.remove('is-solid')); // 打完恢复闪烁
      tl.to({}, { duration: 1.5 });
      tl.call(() => caret.classList.remove('is-on'));    // 闪两拍后熄灭
    },
    reset() {
      if (!played) return;
      played = false;
      // 先杀时间轴再清 DOM：反过来的话被杀之前的那一帧还能再塞一个字符进来
      if (tl) { tl.kill(); tl = null; }
      gsap.set(host, { autoAlpha: 0 });
      [...inner.childNodes].forEach((n) => { if (n !== caret) n.remove(); });
      caret.classList.remove('is-on', 'is-solid');
    },
  };
}

/* 3S 封面屏（scrub，无 pin——封面是 sticky，被堆叠页覆盖）：
 * 滚动 → 画面变暗（cloud→graphite）+ "How we engineer" 水平平行飞出放大
 *      + 三个 S 依次以【打字机】逐字出现（非渐显）。反向滚动可逆。
 * 无 JS / reduced-motion：浅底 + 三行标题静态（S 词由 CSS 直接可见）。 */
function ansCover() {
  const cover = document.querySelector('[data-ans]:not([data-ans-bound])');
  if (!cover) return;
  cover.setAttribute('data-ans-bound', '1');
  const fly = cover.querySelector('[data-ans-fly]');
  const title = cover.querySelector('.ans__title');
  const bg = cover.querySelector('[data-ans-bg]');
  const css = getComputedStyle(document.documentElement);
  const fade = css.getPropertyValue('--gl-ans-fade').trim();
  const typers = [...cover.querySelectorAll('[data-ans-s]')]
    .map((host) => makeTyper(host, '.tt-frame'))
    .filter(Boolean);

  /* 时序（08-21 修）：① 背景图渐显 + 主文案转暗 → ② "OUR ANSWERS" 水平飞出
   * → **飞出走完之后** ③ 三个 S 词依次打字。
   * ⚠️ 三个词的触发读的是 **scrollTrigger 的 progress**，而 progress 是按
   *    时间轴**总时长**归一化的。原来时间轴最长的 tween（飞出，0.05→0.75）就是
   *    时间轴的末尾，所以飞出结束的那一刻 progress 才等于 1——
   *    阈值 0.34 落在飞出只走了 29% 的位置，三件事必然同时发生
   *    （被反馈"这三个动作同时发生"）。
   *    解法：在时间轴末尾补一段空白 tween 把总时长拉长到 1.3，
   *    于是飞出在 progress ≈ 0.58 就结束，后面 42% 的行程专门给三个词。
   *    行程也从 110% 加到 170%，免得前半段被压得太快。
   *    改这里的 duration 时必须回来一起改下面的阈值——它们是同一条时间轴上的刻度。 */
  /* 入场（08-22）：封面从下方升上来的那一屏做淡入 + 逐字拉起。
   * 原来标题走 linesUp 的默认触发（`top 96%` + once），但 .ans 是 **sticky**——
   * ScrollTrigger 记的是它未粘住时的自然位置，于是封面还在半屏外时逐字就跑完了，
   * 滑到这一屏时文案已经是静止的完成态，和上一屏的滚动动作断开（被反馈"衔接不上"）。
   * 挂到这条 scrub 上之后，行程正好是"封面升上来"的一屏：滚动到位＝文案到位，
   * 结束的那一刻主时间轴（start: 'top top'）接手，两段首尾相接、中间没有静止帧。
   * 淡入目标取 .ans__inner 而不是 .ans__title：三个 S 词在窄屏是静态排在标题下方的，
   * 只淡标题会变成"标题在浮现、S 词已经站好了"。 */
  const inner = cover.querySelector('.ans__inner');
  /* 行程按**标题自己**进入视口算，不按封面顶：文案在封面里垂直居中，封面顶碰到屏底时
   * 标题还在屏幕下方 300px 处，用 cover 当 start 会让淡入的前半程发生在观众看不见的地方
   * （实测封面顶走到屏幕中部时 opacity 已经 0.52，而标题此刻才刚露头）。
   * start＝标题顶碰屏底，end＝封面顶到屏顶（＝标题被粘住不再移动、主时间轴接手的那一刻）。
   * 两个不同的元素定首尾，所以要用 endTrigger。 */
  const intro = gsap.timeline({
    scrollTrigger: { trigger: title || cover, start: 'top bottom',
      endTrigger: cover, end: 'top top', scrub: 0.4, invalidateOnRefresh: true },
  });
  /* 淡入占满整段行程的 72%，不是一半：一半的话封面顶刚到屏幕中部（＝这一屏读得最多的
   * 位置）文案就已经全亮，后半屏又变回静止的完成态——那正是要修的问题。 */
  if (inner) intro.fromTo(inner, { opacity: 0 }, { opacity: 1, ease: 'none', duration: 0.72 }, 0);
  if (title && title._glWords) {
    const n = title._glWords.length;
    /* stagger 总量固定 0.36：字数变了也不会把逐字拖出这段行程（0.08 + 0.5 + 0.36 = 0.94，
     * 末尾留 0.06 给到位后的停顿，免得最后一个字刚落、主时间轴就开始转暗）。 */
    intro.to(title._glWords, { yPercent: 0, ease: 'power4.out', duration: 0.5,
      stagger: { each: 0.36 / n } }, 0.08);
  }

  const FLY_END = 0.75 / 1.3; // ≈0.577，飞出结束时的 progress
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: cover, start: 'top top', end: '+=170%', scrub: 0.5, invalidateOnRefresh: true,
      onUpdate: (self) => {
        typers.forEach((t, i) => {
          /* 首个词等飞出完全走完再开始（+0.04 的余量避开 scrub 的缓动尾巴），
           * 之后每个词间隔 0.12 的行程，读起来是"一个接一个"而不是三个一起冒。 */
          if (self.progress > FLY_END + 0.04 + i * 0.12) t.play(); else t.reset();
        });
      },
    },
  });
  // 背景图渐显（灰遮罩固定不变，只是图浮出来）+ 主文案退成低对比背景层
  if (bg) tl.to(bg, { opacity: 1, ease: 'none', duration: 0.5 }, 0);
  if (title) tl.to(title, { color: fade, ease: 'none', duration: 0.45 }, 0.05);
  /* 水平平行飞出：只走 x（不放大——Figma 里飞出行与主标题同字号 130px），y 不动。
   * 位移＝**版心宽 − 这一行的实际文字宽**，也就是让它的右缘正好落在版心右缘。
   * 原来写死 `innerWidth * 0.22`，那是按旧文案 "Our answers" 的宽度凑出来的值；
   * 08-25 换成更长的 "How we engineer" 后，同样的位移会把行尾推出屏幕
   * （实测右缘 1525 > 视口 1440，尾部被 .ans 的 overflow:hidden 切掉）。
   * 按文字宽度反推之后，换任何长度的文案都不用再回来调这个数——
   * 这也正是原注释想表达的"落点右边缘对齐 gutter"。
   * ⚠️ 用 scrollWidth 而不是 getBoundingClientRect().width：后者在 tween 运行中
   *    会带上当前的 x 位移，invalidateOnRefresh 重算时会逐次累加、越飞越远。 */
  if (fly) {
    const shell = cover.querySelector('.ans__inner');
    tl.to(fly, {
      x: () => {
        const avail = shell ? shell.getBoundingClientRect().width : window.innerWidth;
        return Math.max(0, avail - fly.scrollWidth);
      },
      ease: 'power2.inOut', duration: 0.7,
    }, 0.05);
  }
  /* 尾段空白：把时间轴总时长拉到 1.3，让"飞出结束"落在 progress ≈0.58，
   * 后面留给三个 S 词依次打字（见上面 FLY_END 的说明）。 */
  tl.to({}, { duration: 0.55 });
}

/* 首屏背景视频：离开首屏后暂停（08-18 性能优化）。
 * 原来它是纯 autoplay+loop、整页滚动过程中一直解码——1080p 7.3MB 的片子在后台常驻解码，
 * 是"页面发卡"最持续的一项开销。注释里那句"全程不因滚动暂停"说的是 **pin 期间**
 * （标题上拉、白色叠层渐入时画面不能停），离开首屏之后暂停不违背它。
 * trigger 取 header 本身而不是 video：video 在 pin 的 transform 里，位置会被 pin 改写。 */
function heroVideoIdle() {
  const hero = document.querySelector('[data-hero-typer]:not([data-hv-bound])');
  if (hero) {
    hero.setAttribute('data-hv-bound', '1');
    const v = hero.querySelector('.hv__video');
    if (v) {
      /* 首帧占位层的交接（09-02）：只在**视频真的开始出画**之后才淡出它。
       * 判据用 playing + readyState≥3（HAVE_FUTURE_DATA），不用 canplay ——
       * canplay 只保证"能开始播"，此刻往往还没解出第一帧，淡出会闪一下白。 */
      const poster = hero.querySelector('[data-hero-poster]');
      const dropPoster = () => {
        if (!poster || poster.classList.contains('is-off')) return;
        if (v.readyState >= 3 && !v.paused) poster.classList.add('is-off');
      };
      if (poster) {
        v.addEventListener('playing', dropPoster);
        v.addEventListener('timeupdate', dropPoster);   /* playing 偶尔不发，用推进兜一层 */
        dropPoster();
      }
      /* 自动播放被挡时的兜底：iOS 在低电量模式下会无条件拒绝 autoplay，
       * 而任何一次用户手势之后 play() 就会被放行。绑一次性的手势监听，
       * 成功起播就把监听摘掉。这不会绕过任何策略——它等的就是用户自己的第一次交互。 */
      const kick = () => {
        if (!v.paused) return;
        const r = v.play();
        if (r && r.catch) r.catch(() => {});
      };
      ['touchstart', 'pointerdown', 'keydown'].forEach((ev) => {
        window.addEventListener(ev, kick, { once: true, passive: true });
      });
      ScrollTrigger.create({
        trigger: hero, start: 'top bottom', end: 'bottom top',
        onToggle: (self) => {
          if (self.isActive) { const r = v.play(); if (r && r.catch) r.catch(() => {}); }
          else v.pause();
        },
      });
    }
  }
  /* 身份屏的 TVC：idfPin 里已有 onLeave/onEnterBack，但实测滑到后面几屏时它**还在播**
   * （pin 的 onLeave 没兜住每种离场路径）。这里补一条只管"不在视口就暂停"的保险，
   * 进入时不主动播——续播仍交给 idfPin 的 progress 逻辑（>0.5 才该出现画面）。
   * ⚠️ 光挂 onToggle 不够：创建 ScrollTrigger 时它若已处于非激活状态，
   *    onToggle 不会补触发一次（它只在状态**变化**时回调），而这条 video 是 autoplay，
   *    于是页面一加载就播、且永远等不到那次回调（实测滑到 FAQ 它都还在播）。
   *    所以再挂 onRefresh，并在创建后立刻自查一次。 */
  document.querySelectorAll('[data-idf-video]:not([data-vi-bound])').forEach((v) => {
    v.setAttribute('data-vi-bound', '1');
    const host = v.closest('[data-idf]') || v.parentElement;
    const idle = (self) => { if (!self.isActive && !v.paused) v.pause(); };
    const st = ScrollTrigger.create({
      trigger: host, start: 'top bottom', end: 'bottom top',
      onToggle: idle, onRefresh: idle,
    });
    idle(st);
  });
}

/* 3S 抽屉页的体验视频：**看得见就播、被下一页完全盖住才暂停**。
 *
 * ⚠️ 原来是给每条视频各建一个 ScrollTrigger（trigger 就是 video 自己，
 *    start: 'top 92%' / end: 'bottom 8%'）。这在 sticky 页面上是错的：
 *    ScrollTrigger 按元素的**文档流位置**算进出，而这三页是 sticky 的——
 *    页面还粘在屏幕上、画面还占着大半屏，它的文档流位置早就"离开"了，
 *    于是视频提前暂停，用户看到的是一帧定格（被反馈"会卡到一些奇怪的画面"）。
 *    这和 snav 高亮踩过的是同一个坑：pin / sticky 让文档位置与显示时机脱钩，
 *    凡是"现在看得见吗"的判断都必须用 getBoundingClientRect 的**渲染位置**。
 *
 * 判定分两层：
 *   ① 渲染矩形还在视口里吗（离开视口就没必要解码）；
 *   ② 下一页的顶边有没有压过本页视频的顶边——三页是不透明白底、层级递增，
 *      下一页顶 ≤ 视频顶 就意味着这条画面已经被完全盖住，这时候才暂停。
 * 末页没有"下一页"，只按①判断。 */
function sVideos() {
  const vids = [...document.querySelectorAll('[data-s-video]:not([data-sv-bound])')];
  if (!vids.length) return;
  vids.forEach((v) => v.setAttribute('data-sv-bound', '1'));
  const pairs = vids.map((v) => {
    const sw = v.closest('.sw');
    const nextSw = sw && sw.nextElementSibling;
    return { v, next: nextSw ? nextSw.querySelector('.spage') : null };
  });
  const sync = () => {
    pairs.forEach(({ v, next }) => {
      const r = v.getBoundingClientRect();
      let show = r.bottom > 0 && r.top < window.innerHeight && r.height > 0;
      if (show && next) {
        const n = next.getBoundingClientRect();
        if (n.top <= r.top + 1) show = false; // 已被下一页完全遮盖
      }
      if (show) {
        if (v.paused) { const pr = v.play(); if (pr && pr.catch) pr.catch(() => {}); }
      } else if (!v.paused) v.pause();
    });
  };
  /* ⚠️ **每个视频绑自己所在的段落**（09-01 改）。原来只建一个 trigger、挂在
   *    `vids[0].closest('.ansx')` 上——那是 3S 那一段。当时所有 data-s-video 都在
   *    3S 里，所以成立；09-01 认证段的静帧换成视频后就不成立了：滚到认证段时
   *    3S 那个 trigger 早已不活跃，sync 根本不会被调用，那个视频要么永不播、
   *    要么播了永不停。
   *    用 Set 去重宿主：3S 三个视频共处一个 .ansx，只需要一个 trigger。
   *    sync 本身遍历全部 pairs、幂等，多个 trigger 重复调用没有副作用。 */
  const hosts = new Set(vids.map((v) => v.closest('.ansx') || v.closest('.gl-section') || document.body));
  /* onRefresh + 立即自查：trigger 若在**已经不活跃**的状态下创建，onToggle 不会触发
   * （踩过：首屏视频因此一直播到底）。 */
  hosts.forEach((host) => {
    ScrollTrigger.create({
      trigger: host, start: 'top bottom', end: 'bottom top',
      onUpdate: sync, onToggle: sync, onRefresh: sync,
    });
  });
  sync();
}

/* sag 计量层（洞察墙第一条）：把画面里正在发生的下垂**翻译成时间与状态**——
 * 右下读数 YEAR 01→10 + 状态词 Aligned / Drifting / Leaning。
 *
 * 沿革（重要，换素材时要一起判断）：
 *   08-21 客户反馈"原片里支架的角度几乎没有变化，读不出随时间累积的下垂"，
 *         当时的做法是在读数之外再给画面叠一层人工倾斜（0→0.8° + scale 3% 防露边）。
 *   08-25 换了新素材（Sag-h264）——**新片自带下垂**，首尾帧实测目测有 8–10° 的变化，
 *         是那层人工倾斜的十倍以上。两者叠加会让画面歪得不自然，所以人工倾斜整层撤掉，
 *         这里只留读数。素材自己会说话时，就不该再替它说一遍。
 *   ⚠️ 如果以后又换回"角度几乎不变"的素材，需要把那层加回来：
 *      gsap.set(video, { rotation: p * 0.8, scale: 1 + p * 0.03,
 *                        transformOrigin: '100% 50%', force3D: true })
 *      —— transformOrigin 取右侧墙面那个支点（悬臂自由端在左，所以左端下沉），
 *      且必须写在 video 元素上、不能写 .obs-card（卡片的 xPercent/scale 是切换动画在用的，
 *      两者都写 transform 会互相覆盖）。
 *
 * ⚠️ 驱动源取**视频自己的 currentTime / duration**，不另起计时器：
 *    obsx 的轮播每次切回这一条都会 `currentTime = 0`（见 playOnly），
 *    独立计时器会和画面对不上。
 * ⚠️ rAF 只在这条视频真的在播时才跑（play/pause 事件开关），
 *    否则轮播到另外两条时它还在空转，白耗电。
 */
function sagGauge() {
  const host = document.querySelector('[data-sag]:not([data-sag-bound])');
  if (!host) return;
  host.setAttribute('data-sag-bound', '1');
  const card = host.closest('.obs-card');
  const video = card && card.querySelector('[data-obsx-video]');
  const yEl = host.querySelector('[data-sag-year]');
  const sEl = host.querySelector('[data-sag-state]');
  if (!video || !yEl || !sEl) return;

  /* 年数与状态词从 DOM 读，缺省时沿用交付包的原值。
   * 它们是画面右下角实际显示的读数文案，属于内容而不是动效参数，
   * 所以要能在后台改；但**不要**改成角度数字——
   * 见下方 STATES 的注释：拿未经核实的角度量化竞品缺陷有合规风险。 */
  const YEARS = parseInt(host.dataset.sagYears, 10) || 10;      // 年份是事实陈述（文案本身就写着 "Year after year"）
  /* 程度用**状态词**而不是角度数字：普通支架 10 年会垂多少度，品牌没有这个数据，
   * 编一个具体角度去量化竞品缺陷站不住（详见 partial 里的说明）。
   * 三个词都出自品牌自己的文案：drift 见 FAQ「no sagging, no drift」，
   * lean 见本屏洞察句「screens start to lean」。 */
  const STATE_WORDS = (host.dataset.sagStates || 'Aligned|Drifting|Leaning')
    .split('|').map((w) => w.trim()).filter(Boolean);
  /* 阈值按词数均分，所以后台改成两个词或四个词都成立 */
  const STATES = STATE_WORDS.map((w, i) => [(i + 1) / STATE_WORDS.length + 0.01, w]);
  let raf = 0;

  /* 读数只有 10 档（年）与 25 档（角度），每帧都写 DOM 是白做——
   * 缓存上一次的字符串，只在真的变了才写。
   * 不是为了帧率（实测三种状态都是稳 60fps、long task 0，rAF 开销在噪声以下），
   * 是为了少一点无谓的样式重算，移动端省电。
   * rotation 每帧都给：它走 GPU 合成（force3D），而且降频会让倾斜看起来一顿一顿。 */
  /* peak：读数与倾斜**只增不减**。
   * 08-22 起这条视频没有 loop 属性了（洞察墙改成"播完再切"，靠 ended 推进），
   * 所以不会再出现"回到第 2 年"的问题；但仍然保留 peak，因为：
   *   · 视频播完停在末尾时，读数要稳稳停在 YEAR 10 / LEANING，不能因为
   *     currentTime 的浮点抖动而跳回 09；
   *   · "垂了十年"本来就不该自己弹回去——不可逆是这个隐喻的一部分。
   * ⚠️ 原来是靠 `video.pause()` 来定格的，那样 `ended` 永远不会触发，
   *    轮播会卡在第一条上（去掉 loop 之后才暴露出来）。现在只锁读数、不动播放。
   * play 事件里重置 peak：每次轮播切回这一条，重新走一遍十年。 */
  let lastY = '', lastS = '', peak = 0;
  const paint = () => {
    const d = video.duration || 5;
    const raw = Math.min(1, Math.max(0, video.currentTime / d));
    peak = Math.max(peak, raw);
    const p = peak;
    const y = String(Math.max(1, Math.ceil(p * YEARS))).padStart(2, '0');
    const s = (STATES.find((x) => p < x[0]) || STATES[STATES.length - 1])[1];
    if (y !== lastY) { yEl.textContent = y; lastY = y; }
    if (s !== lastS) { sEl.textContent = s; lastS = s; }
  };
  const loop = () => { paint(); raf = requestAnimationFrame(loop); };
  const start = () => { if (!raf) raf = requestAnimationFrame(loop); };
  const stop = () => { if (raf) { cancelAnimationFrame(raf); raf = 0; } paint(); };

  video.addEventListener('play', () => { peak = 0; start(); });
  video.addEventListener('pause', stop);
  video.addEventListener('emptied', stop);
  /* seeked 也要重画：轮播切回这一条时 playOnly 先 `currentTime = 0` 再 play，
   * 而暂停状态下 rAF 是停的——不听 seeked 的话读数会停在上一次的值，
   * 与画面（已经跳回第一帧）对不上。 */
  video.addEventListener('seeked', paint);
  video.addEventListener('loadedmetadata', paint);
  if (!video.paused) start(); else paint();
}

/* 洞察卡 · 原位切换：三条洞察在同一位置轮播，左上 tab 同步高亮、可点选。
 * 无 JS / reduced-motion：三张卡纵向静态排列（CSS 默认）。 */
function obsxPin() {
  const sec = document.querySelector('[data-obsx]:not([data-obsx-bound])');
  if (!sec) return;
  sec.setAttribute('data-obsx-bound', '1');
  const panels = [...sec.querySelectorAll('[data-obsx-panel]')];
  const tabs = [...sec.querySelectorAll('[data-obsx-tab]')];
  const videos = panels.map((p) => p.querySelector('[data-obsx-video]'));
  const progFill = sec.querySelector('[data-obsx-prog]');
  if (panels.length < 2) return;
  sec.classList.add('obsx--deck'); // 切到叠放模式（无 JS 时保持纵向静态排列）
  /* 初始态：当前那张在位（scale 1），其余停在右侧画外并预先缩到 0.84。
   * 不用 autoAlpha 隐藏——参考站全程 opacity 恒为 1，靠"出画"而不是淡出。 */
  gsap.set(panels[0], { xPercent: 0, scale: 1 });
  gsap.set(panels.slice(1), { xPercent: 100, scale: 0.84 });
  let cur = 0;
  /* ⚠️ 每条都先强制解码一帧：preload="auto" 在真实浏览器里仍可能让**暂停中**的那条渲染成空白
   * （被反馈过两次"视频没显示出来"）。seek 一小段会迫使解码器出一帧，暂停时也就有画面了。 */
  videos.forEach((v, k) => {
    if (!v || k === 0) return; // 第 0 条进视口就会 play，不需要
    const seekOne = () => { try { v.currentTime = 0.05; } catch (e) { /* 元数据未就绪，忽略 */ } };
    if (v.readyState >= 1) seekOne();
    else v.addEventListener('loadedmetadata', seekOne, { once: true });
  });
  /* 只播当前那条：三路 1080p 同时解码在低端机上会掉帧，而且切回来要从头看 */
  const playOnly = (i) => videos.forEach((v, k) => {
    if (!v) return;
    if (k === i) { v.currentTime = 0; const r = v.play(); if (r && r.catch) r.catch(() => {}); }
    else v.pause();
  });
  /* 轮播节奏（改过三轮，都记下来）：
   *   固定 6s → 「播完再切」（时长即停留）→ **「最小停留 + 整数倍循环」**（09-02）
   * 固定 6s 的问题是三条素材时长不一（5.07 / 3.53 / 7.50s），短的 loop 两遍、
   * 长的只播到七成就被切走；改成播完再切之后时长就是停留时间，但最短那条
   * GRIND 只有 3.53s —— 反馈"这三个自动切换太快了"。
   * 现在给一个最小停留 MIN_DWELL，不足的按**整数倍**循环补到刚好超过它：
   *   SAG 5.07×2 = 10.1s · GRIND 3.53×2 = 7.1s · COMPROMISE 7.50×1 = 7.5s（一轮 24.7s）
   * 取整数倍而不是"播完停住等够时间"：整数倍的切换点落在一个循环的末尾，
   * 画面正好回到起始帧附近，不会出现"最后一帧静止几秒"的卡顿感。
   * video 因此要加回 loop 属性（见 partial），推进也从 ended 改回 timer——
   * loop 状态下 ended 永远不会触发。 */
  const MIN_DWELL = 7;
  const FALLBACK_PAD = 1500;
  let timer = 0;
  let prog = null;
  /* 进度条跟着**这一条的时长**走，读数和真实等待时间一致 */
  const progDur = (i) => {
    const v = videos[i];
    const d = v && v.duration;
    const len = (d && isFinite(d) && d > 0.3) ? d : 6;
    /* 不足最小停留就按整数倍循环补上去 */
    return len >= MIN_DWELL ? len : len * Math.ceil(MIN_DWELL / len);
  };
  const restartProg = () => {
    if (prog) prog.kill();
    if (!progFill) return;
    prog = gsap.fromTo(progFill, { scaleX: 0 },
      { scaleX: 1, duration: progDur(cur), ease: 'none' });
  };
  /* timer 现在是**主推进器**（09-02 起 video 带 loop，ended 不再触发）。
   * ⚠️ 每次切换都必须重新武装，不能只在 start() 里设一次：
   * 否则上一条的 timer 会跨条继续跑——实测 SAG(5.03s) 的 timer 是 6.53s，
   * SAG 播完切到 GRIND 之后它还剩 1.5s，到期又切一次，
   * GRIND 就只播了 36% 就被顶走（表现为"第二条特别短"）。 */
  const armFallback = () => {
    if (timer) { clearTimeout(timer); timer = 0; }
    timer = setTimeout(() => show((cur + 1) % panels.length),
      progDur(cur) * 1000 + FALLBACK_PAD);
  };
  const show = (i) => {
    if (i === cur) return;
    /* 中心焦点轮播（照 addverb.ai 实测轨迹还原）：
     * 旧画面往左滑出画外并缩到 0.84，新画面从右侧画外滑入并放大回 1，两者同时进行。
     * 参考站量到的手感（扣掉它开头约 370ms 的准备停顿后）：108ms 走 26% → 230ms 64%
     * → 376ms 85% → 595ms 96%，1.1s 收尾。前段极陡、尾巴极长，是 expo.out 而非 power3.out。
     * 那 370ms 停顿是它的实现开销（Framer variant 切换），不是设计意图，我们不复制——
     * 点击要跟手。全程不动 opacity（靠出画，不靠淡出）。 */
    const DUR = 1.4;
    const EASE = 'expo.out';
    gsap.to(panels[cur], { xPercent: -100, scale: 0.84, duration: DUR, ease: EASE });
    gsap.fromTo(panels[i], { xPercent: 100, scale: 0.84 },
      { xPercent: 0, scale: 1, duration: DUR, ease: EASE });
    tabs.forEach((t, k) => {
      t.classList.toggle('is-active', k === i);
      t.setAttribute('aria-selected', k === i ? 'true' : 'false');
    });
    playOnly(i);
    cur = i;
    restartProg();
    armFallback();
  };
  /* 自动轮播（08-17 改）：这一屏原来是 pin 住、必须滑完三条才放行——
   * 三条洞察是并列的观察，没有先后依赖，不该强制用户走完流程。
   * 现在改成进视口自动轮播 + 可点选，页面滚动完全自由。 */
  const stop = () => {
    if (timer) { clearTimeout(timer); timer = 0; }
    if (prog) prog.pause();
    const v = videos[cur];
    if (v && !v.paused) v.pause();   // hover 暂停要连视频一起停，否则它照样播完就切走
  };
  const start = () => {
    const v = videos[cur];
    if (v && v.paused) { const r = v.play(); if (r && r.catch) r.catch(() => {}); }
    armFallback();   // setTimeout 而不是 setInterval：正常路径由 ended 推进，这里只兜一次
    if (prog) prog.play(); else restartProg();
  };
  /* ended 监听保留作**双保险**：万一某个浏览器忽略了 loop 属性，
   * 它会在第一遍播完时触发，按原逻辑推进，不至于卡死。
   * 正常路径（loop 生效）下它永远不会被调用。 */
  videos.forEach((v, k) => {
    if (!v) return;
    v.addEventListener('ended', () => {
      if (k !== cur) return;
      show((cur + 1) % panels.length);
    });
  });
  ScrollTrigger.create({
    trigger: sec, start: 'top 85%', end: 'bottom 15%',
    onToggle: (self) => {
      if (self.isActive) { playOnly(cur); restartProg(); start(); }
      else { stop(); videos.forEach((v) => v && v.pause()); }
    },
  });
  /* 点选：切过去并重置计时（否则刚点完可能立刻被自动轮播切走） */
  tabs.forEach((tab, i) => tab.addEventListener('click', () => { show(i); start(); }));
  /* 悬停暂停只挂在 **tab 行**上，不是整段（08-18 修）：
   * 用滚轮滑过来的时候鼠标基本就停在画面中央，整段 mouseenter 一触发就 stop——
   * 轮播卡死、第一条视频自己 loop 三四遍才动（被反馈的就是这个）。
   * 画面区 hover 不该暂停：用户正在看轮播，不是在读需要时间的长文。
   * tab 行保留暂停：那是他准备点选，别在抬手前切走。
   * 用 mouseenter/mouseleave 而不是 pointerenter：鼠标场景两者等价、前者兼容性更稳。 */
  /* 同上：触屏上 mouseenter 会被合成、mouseleave 不来，一碰就永久停住。 */
  const ctrls = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ? sec.querySelector('.obsx__controls') : null;
  if (ctrls) {
    ctrls.addEventListener('mouseenter', stop);
    ctrls.addEventListener('mouseleave', () => { if (timer === 0) start(); });
  }
}

function thirdIn() {
  const sec = document.querySelector('[data-third]:not([data-third-bound])');
  if (!sec) return;
  sec.setAttribute('data-third-bound', '1');
  const title = sec.querySelector('.proto__third-title');
  const img = sec.querySelector('.third__media img');
  const cols = [...sec.querySelectorAll('.endorse')];
  /* 拆成两条时间轴，各自绑到"自己什么时候露出来"（08-19 修）：
   * 原来共用一个 trigger: sec / top 80%，那一刻三栏还在屏外 624px（实测），
   * stagger 全在看不见的地方跑完——现象就是"入场没实现"。 */
  const tl = gsap.timeline({ scrollTrigger: { trigger: sec, start: 'top 85%', once: true } });
  if (title) tl.from(title, { y: 28, autoAlpha: 0, duration: 0.72, ease: 'power3.out' }, 0);
  /* scale 给 img 而不是 .third__media：容器带圆角与 overflow，缩容器会把圆角也缩了 */
  if (img) tl.from(img, { scale: 1.06, autoAlpha: 0, duration: 1.05, ease: 'power3.out' }, 0.12);
  /* 三栏在大图下方 676px，必须用自己的触发点（grid 顶进视口 92%）才看得到错峰 */
  const grid = sec.querySelector('.proto__third-grid');
  if (cols.length && grid) {
    gsap.set(cols, { autoAlpha: 0, y: 24 });
    /* 窄屏把触发点从 92% 推到 60%（09-01）：这一档有黑→白的转场（pxaFade），
     * 92% 时底色还在最黑那一段，而三格里的 logo 是图片——黑白的 MUSE 与 IEA
     * 在暗底上没法读，文字能跟着换色、图片不能（invert 那条路品牌已经否过）。
     * 60% 对应的滚动位置刚过转场终点，底色已经转白。
     * 桌面维持 92%：那边没有转场，且注释里那条"三栏在大图下方 676px、
     * 必须用自己的触发点才看得到错峰"的理由仍然成立。 */
    const narrow = window.matchMedia('(max-width: 1199px)').matches;
    gsap.to(cols, { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.09, ease: 'power3.out',
      scrollTrigger: { trigger: grid, start: narrow ? 'top 60%' : 'top 92%', once: true } });
  }
}

/* ══════════════════════════════════════════════════════════════════
 * 启动与 Shopify 主题编辑器生命周期
 * ══════════════════════════════════════════════════════════════════ */

/* 清掉指向已脱离文档的元素的 ScrollTrigger。
 *
 * 主题编辑器改动某个 section 时会整块替换它的 DOM。旧的 ScrollTrigger 还注册着，
 * trigger 指向已被移除的节点，于是位置判定用的是一个不在文档里的元素——
 * 本页有 3 处 pin 和一段 sticky 堆叠，表现是这几段的粘停位置全错。
 *
 * kill(true) 会连 pin 产生的 pin-spacer 包装层一起还原，不留残骸。 */
function killDetached() {
  ScrollTrigger.getAll().forEach((st) => {
    const t = st.trigger;
    if (t && !document.documentElement.contains(t)) st.kill(true);
  });
}

/* pin 在主题编辑器里的处理：
 * 编辑器点选某个 section 会滚动定位到它。若此时该段正被 pin（position: fixed），
 * 编辑器算出的目标位置与实际渲染位置脱钩，会把画面顶到错的地方。
 * 所以点选期间禁用 pin，取消点选后恢复。顾客侧不受影响。 */
function setPinsEnabled(on) {
  ScrollTrigger.getAll().forEach((st) => {
    if (!st.pin) return;
    if (on) {
      st.enable();
    } else {
      /* disable 的第一个参数是 revert。必须传 true：
       * 传 false 只是停止更新，pin 产生的 position: fixed 与 pin-spacer 都还留着，
       * 元素会卡在屏幕上不动；传 true 才会还原成正常文档流，
       * 编辑器算出的滚动目标位置才和实际渲染位置一致。 */
      st.disable(true);
    }
  });
}

function boot() {
  init();
  ScrollTrigger.refresh();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

/* 字体是 font-display: swap，落位时行宽会变，逐字/逐行拆分的度量要重算。
 * 只做一次，不用 MutationObserver 之类的持续监听。 */
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => ScrollTrigger.refresh());
}

/* header 高度会随窗口宽度变（桌面/移动两套版式），跟着重测并刷新。 */
let resizeTimer = null;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    measureChrome();
    ScrollTrigger.refresh();
  }, 200);
}, { passive: true });

/* ── 主题编辑器事件 ─────────────────────────────────────────────── */

document.addEventListener('shopify:section:load', (e) => {
  /* 换掉的可能是 header（高度变了）也可能是某个 gl- section。
   * 两种情况都要：清残骸 -> 重测框架 -> 重跑绑定 -> 刷新位置。
   * 新注入的 DOM 没有 data-*-bound 标记，所以 init() 会接上它；
   * 页面上其它已绑定的段则被守卫跳过，不会重复绑定。 */
  killDetached();
  init();
  ScrollTrigger.refresh();
});

document.addEventListener('shopify:section:unload', () => {
  killDetached();
  ScrollTrigger.refresh();
});

document.addEventListener('shopify:section:select', () => setPinsEnabled(false));
document.addEventListener('shopify:section:deselect', () => {
  setPinsEnabled(true);
  ScrollTrigger.refresh();
});

/* 块级点选同理（3S 三页、洞察卡、生产环节都是 block） */
document.addEventListener('shopify:block:select', () => setPinsEnabled(false));
document.addEventListener('shopify:block:deselect', () => {
  setPinsEnabled(true);
  ScrollTrigger.refresh();
});

}  /* ← if (!gsap || !ScrollTrigger) 的 else 分支结束 */

/* ============================================================================
 * 生产段的黑↔白转场（08-31，仅窄屏）
 *
 * 窄屏这一段是**渐变黑**（桌面是满屏实拍背景图，窄屏把图隐藏了、改成黑渐变，
 * 见 components.css 里 max-width:1199px 那块）。黑段前后都是白段
 * （founder 引言 / 认证段），交界处是硬切——反馈"会跟前一个板块、后一个板块的
 * 白色有很大的反差……从前一个板块进来的时候，整个页面可以一起白渐变到黑，
 * 离开进入后一个板块的时候 整个页面背景又从黑渐变成白"。
 *
 * 做法与身份屏那段白→琥珀的转场同源：**按滚动进度写 backgroundColor**。
 * 试过纯 CSS 的渐变过渡带，放不下——实测 founder 底部只剩 32px 空白、
 * 认证段顶部 0px，交界处没有可用的空间；再加空白段又会平白拉长页面。
 *
 * 只染背景不够：founder 的引言是 ink 深字，底色变黑就读不出来了，
 * 所以引言的 color 跟着一起补间（ink → paper）。
 * 琥珀色的高亮词与署名两边都不染——它在白底和深底上都够亮
 * （与 snav 的 is-on-dark 同一个判断）。
 * 取景框线交给 CSS 的 .fdr.is-dark（有 transition，不需要 JS 补间）。
 * ============================================================================ */
function pxaFade() {
  const stage = document.querySelector('.pxa__stage:not([data-pxaf-bound])');
  if (!stage) return;
  const pxa = stage.closest('.pxa');
  const fdr = document.querySelector('.fdr');
  if (!pxa || !fdr) return;
  stage.setAttribute('data-pxaf-bound', '1');

  /* 只在窄屏成立：桌面这一段是满屏实拍图，前后本来就靠图片过渡，不存在硬切。
   * 断点跟 components.css 里那块提亮层同一个值（1199px），两边必须一致。 */
  const mq = window.matchMedia('(max-width: 1199px)');

  const rootCS = getComputedStyle(document.documentElement);
  const paper = rootCS.getPropertyValue('--gl-paper').trim();
  const ink = rootCS.getPropertyValue('--gl-ink').trim();

  const quote = fdr.querySelector('.fdr__quote');
  const third = pxa.querySelector('.proto__third');
  const thirdTitle = pxa.querySelector('.proto__third-title');
  const anchor = thirdTitle || third;

  const mixBg = gsap.utils.interpolate(paper, ink);
  const mixQuote = gsap.utils.interpolate(ink, paper);

  /* 四个换挡点（文档坐标）。每次 refresh 重量一次——它们随视口高、
   * 段落高度、以及 3S 那边的 pin 行程变化。 */
  let yA = 0, yB = 0, yC = 0, yD = 0;
  const measure = () => {
    const sc = window.scrollY || window.pageYOffset;
    const vh = window.innerHeight;
    const sr = stage.getBoundingClientRect();
    const tr = (third || anchor).getBoundingClientRect();
    yA = sr.top + sc - vh * 0.75;   /* 进场起点：stage 顶到达视口 75% */
    yB = sr.top + sc;               /* 进场终点：stage 顶对齐视口顶 */
    /* 出场行程**锚在认证段自己身上**（09-01 第二次改）：
     *   起点 = 认证段顶到达视口 60%（它已露出约 0.4 屏：标题 + 视频上半）
     *   终点 = 认证段顶到达视口 20%
     * 反馈"渐变白得有点太快了。能不能让我下滑到认证段漏出这一部分，
     * 背景才开始变白"——上一版起点锚在 stage 底（比这里早约 500px），
     * 卡片还占着屏幕背景就已经中灰了。
     * 这样锚还有个好处：行程与"认证段露出多少"直接挂钩，
     * 不再受生产段卡片高度、过渡带宽窄的影响（那条带子刚为了间距收到 8vh）。 */
    yC = tr.top + sc - vh * 0.6;
    yD = tr.top + sc - vh * 0.2;
    if (yB <= yA) yB = yA + 1;
    if (yD <= yC) yD = yC + 1;
  };

  /* ⚠️ 用一条手动分段的曲线，而不是两个 fromTo（08-31 改）。
   * 进场和出场都要写 .pxa 这同一个元素，两个 fromTo 会互相抢：
   * 后创建的那个 immediateRender 会把 from 值盖上去，进场就永远从黑开始。
   * 手动分段还有个好处——中间那段"全黑"是显式的，不依赖两个 ScrollTrigger
   * 各自的边界行为恰好对得上。 */
  const paint = () => {
    const y = window.scrollY || window.pageYOffset;
    let c, qp = -1;
    /* 出场进度，单独算：认证段的文字要按它反向换色（见下） */
    const op = y <= yC ? 0 : (y >= yD ? 1 : (y - yC) / (yD - yC));
    if (y <= yA) { c = paper; qp = 0; }
    else if (y < yB) { qp = (y - yA) / (yB - yA); c = mixBg(qp); }
    else if (y <= yC) { c = ink; qp = 1; }
    else if (y < yD) { c = mixBg(1 - op); }
    else { c = paper; }
    /* 认证段的**深色态**：转场推后到"认证段已露出 0.4 屏"之后，它的标题
     * （thirdIn 在 'top 85%' 就淡入了）会先落在还是黑的底上——深字配黑底等于没有。
     * 所以让这一段跟着反向换色：底黑时文字浅、底白时文字深，
     * 与进场那头 founder 的引言完全对称。
     * 阈值 0.55：底色过了中点再换，换色的一刻两边对比都还够。
     * 用 class + CSS transition 而不是逐帧补间——要染的是标题、三个小标题、
     * 三段说明共七处，切一个类比补七条曲线干净得多。 */
    if (third) third.classList.toggle('is-dark', y > yA && op < 0.55);
    /* founder 与整个生产段是**同一层底色**：反馈"应该是生产段的背景+上一段，
     * 整个屏的背景都是渐变"。stage 自己不再有底色（见 components.css）。 */
    fdr.style.backgroundColor = c;
    pxa.style.backgroundColor = c;
    if (quote && qp >= 0) {
      /* 引言用 power3（qp³）而底色用线性：两条线性曲线会在中点撞成"灰底灰字"
       * （实测 50% 处 底 158 / 字 135，差 23）。三次方让字在前 2/3 行程几乎不动、
       * 保持深色，等底色暗下去再迅速转白，低对比的一刻被推到引言已滚出视口之后。 */
      quote.style.color = mixQuote(qp * qp * qp);
    }
    /* 取景框线换向：CSS 那边有 transition，切类即可。
     * 阈值 0.45 而不是 0.5——线是 45% 透明度的，比整块底色更早"看不清"。 */
    fdr.classList.toggle('is-dark', qp > 0.45);
  };

  const clear = () => {
    fdr.style.removeProperty('background-color');
    pxa.style.removeProperty('background-color');
    if (quote) quote.style.removeProperty('color');
    fdr.classList.remove('is-dark');
    if (third) third.classList.remove('is-dark');
  };

  let st = null;
  const build = () => {
    if (st) { st.kill(); st = null; }
    clear();
    if (!mq.matches) return;
    measure();
    st = ScrollTrigger.create({
      /* 范围放得比转场本身宽：paint 内部自己分段，ScrollTrigger 只负责
       * "在这一带滚动时把我叫起来"。 */
      trigger: fdr, start: 'top bottom', endTrigger: anchor, end: 'bottom top',
      onUpdate: paint,
      onRefresh: () => { measure(); paint(); },
    });
    paint();
  };

  build();
  /* 跨断点重建：染色目标与是否染都随断点变，resize 到桌面必须把内联样式清掉，
   * 否则 founder 会带着一身黑留在桌面版上。 */
  if (mq.addEventListener) mq.addEventListener('change', build);
  else if (mq.addListener) mq.addListener(build);
}
